import { useCallback, useEffect, useMemo, useState } from 'react'
import { Check, Search, Trash2 } from 'lucide-react'
import { Panel } from '@/components/erp/primitives'
import { ErrorStrip, LoadingBlock, Note } from '@/shared/ui/states'
import { explain } from '@/shared/api/problem'
import { cn } from '@/lib/utils'
import type {
  SurveyLinkDto, SurveyLinkSuggestionDto, SurveyLinkableTypeDto, SurveyResponseDto,
} from '@/shared/api/types'
import {
  addSurveyLink, listLinkableTypes, listSurveyLinks, removeSurveyLink, suggestSurveyLinks,
} from './api'
import { linkTypeImplication, linkTypeLabel } from './vocab'
import { StepField } from './ConfigChoice'

/**
 * Step 3 — what this poll is about.
 *
 * ## The picker is narrowed by the server, and that is the whole feature
 *
 * PollSphere's equivalent listed every candidate in the deployment, so a
 * "Langata MP" survey could link a presidential candidate and nothing refused
 * it. Here `GET /surveys/{id}/link-suggestions` asks the owning feature, and
 * `politics` — the only one that knows a seat type implies a unit level —
 * returns candidates on this poll's own geographic branch and no others.
 *
 * There is no client-side filter in this file. A list narrowed here would be a
 * second implementation of a rule that already exists on the server, and the
 * two would disagree the first time a seat rule changed.
 *
 * ## The type list is served too
 *
 * `GET /surveys/linkable-types` returns only the types this deployment can
 * actually resolve. A hardcoded list of six would offer the ones whose owning
 * module is switched off, and picking one of those produces a link that renders
 * as an unnamed row forever.
 */
export function ConfigLinksStep({ poll, canUpdate }: {
  poll: SurveyResponseDto
  canUpdate: boolean
}) {
  const [types, setTypes] = useState<SurveyLinkableTypeDto[] | null>(null)
  const [typeCode, setTypeCode] = useState('')
  const [query, setQuery] = useState('')
  const [suggestions, setSuggestions] = useState<SurveyLinkSuggestionDto[]>([])
  const [suggestLoading, setSuggestLoading] = useState(false)
  const [linked, setLinked] = useState<SurveyLinkDto[] | null>(null)
  const [busyRef, setBusyRef] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const reloadLinks = useCallback(async () => {
    try {
      setLinked(await listSurveyLinks(poll.id))
    } catch (e) {
      setError(explain(e))
    }
  }, [poll.id])

  useEffect(() => {
    let alive = true
    listLinkableTypes()
      .then((t) => {
        if (!alive) return
        setTypes(t)
        // CANDIDATE first when it exists — it is what nine of ten polls link —
        // but never hardcoded as the only option, and never assumed present.
        setTypeCode((current) => current
          || t.find((x) => x.typeCode === 'CANDIDATE')?.typeCode
          || t[0]?.typeCode
          || '')
      })
      .catch((e) => { if (alive) setError(explain(e)) })
    return () => { alive = false }
  }, [])

  useEffect(() => { void reloadLinks() }, [reloadLinks])

  useEffect(() => {
    if (!typeCode) return
    let alive = true
    setSuggestLoading(true)
    const timer = setTimeout(() => {
      suggestSurveyLinks(poll.id, { typeCode, q: query || undefined, limit: 100 })
        .then((s) => { if (alive) setSuggestions(s) })
        .catch(() => { if (alive) setSuggestions([]) })
        .finally(() => { if (alive) setSuggestLoading(false) })
    }, 250)
    return () => { alive = false; clearTimeout(timer) }
  }, [poll.id, typeCode, query, linked])

  const selectedType = useMemo(
    () => types?.find((t) => t.typeCode === typeCode) ?? null,
    [types, typeCode],
  )

  async function toggle(suggestion: SurveyLinkSuggestionDto) {
    setBusyRef(suggestion.ref)
    setError(null)
    try {
      if (suggestion.linked) {
        const existing = (linked ?? []).find(
          (l) => l.ref === suggestion.ref && l.typeCode === suggestion.typeCode,
        )
        if (existing) await removeSurveyLink(poll.id, existing.id)
      } else {
        await addSurveyLink(poll.id, { typeCode: suggestion.typeCode, ref: suggestion.ref })
      }
      await reloadLinks()
    } catch (e) {
      setError(explain(e))
    } finally {
      setBusyRef(null)
    }
  }

  async function remove(link: SurveyLinkDto) {
    setBusyRef(link.ref)
    setError(null)
    try {
      await removeSurveyLink(poll.id, link.id)
      await reloadLinks()
    } catch (e) {
      setError(explain(e))
    } finally {
      setBusyRef(null)
    }
  }

  return (
    <div className="space-y-4">
      <Panel title="What this poll is about"
        description="Candidates, programmes and the rest of the platform">
        <div className="space-y-4">
          <ErrorStrip message={error} />

          <Note>
            This is a different statement from the links on an individual question. A ballot
            option links to the candidate it names; the POLL links to the people and programmes
            it was commissioned to measure. Deriving one from the other would make a poll stop
            being about somebody the moment its ballot question was reworded.
          </Note>

          {linked === null ? <LoadingBlock label="Reading the links…" /> : linked.length === 0 ? (
            <p className="text-erp-sm text-muted-foreground">
              Nothing linked yet. A poll with no links still works — this decides whose records
              and which filters can find it afterwards.
            </p>
          ) : (
            <ul className="border border-border rounded-md divide-y divide-border">
              {linked.map((link) => (
                <li key={link.id} className="flex items-center gap-2 px-3 py-2">
                  <span className="text-erp-xs uppercase tracking-wide text-muted-foreground w-24 shrink-0">
                    {linkTypeLabel(link.typeCode)}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="text-erp-sm block truncate">
                      {link.resolved ? link.label : (
                        <span className="text-amber-700">
                          Cannot be named — {link.ref}
                        </span>
                      )}
                    </span>
                    {link.secondaryLabel && (
                      <span className="text-erp-xs text-muted-foreground block truncate">
                        {link.secondaryLabel}
                      </span>
                    )}
                  </span>
                  {canUpdate && (
                    <button type="button" className="erp-btn-ghost h-7 px-2 shrink-0"
                      disabled={busyRef === link.ref}
                      title="Remove this link" onClick={() => remove(link)}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}

          {linked?.some((l) => !l.resolved) && (
            <Note tone="bad">
              A link above could not be named by the feature that owns it — it may have been
              deleted, or that module may be switched off in this deployment. The row is kept
              rather than hidden, because a link that silently disappeared would be worse.
            </Note>
          )}
        </div>
      </Panel>

      {canUpdate && (
        <Panel title="Add a link" description="Narrowed to what this poll's scope allows">
          <div className="space-y-3">
            {types === null ? <LoadingBlock label="Reading what can be linked…" /> : (
              <>
                <StepField label="Type"
                  hint={linkTypeImplication(typeCode) || undefined}>
                  <div className="flex flex-wrap gap-1.5">
                    {types.map((t) => (
                      <button key={t.typeCode} type="button"
                        onClick={() => { setTypeCode(t.typeCode); setQuery('') }}
                        className={cn(
                          'h-8 px-3 text-erp-xs rounded border transition-colors',
                          t.typeCode === typeCode
                            ? 'bg-chocolate text-white border-chocolate'
                            : 'bg-background border-border hover:bg-muted',
                        )}>
                        {linkTypeLabel(t.typeCode)}
                      </button>
                    ))}
                  </div>
                </StepField>

                {selectedType && !selectedType.searchable ? (
                  <Note>
                    {linkTypeLabel(selectedType.typeCode)} has no typeahead in this deployment,
                    so there is nothing to browse. The feature that owns it has not published a
                    search.
                  </Note>
                ) : (
                  <>
                    <div className="relative">
                      <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <input className="erp-input h-9 w-full pl-8" value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder={`Search ${linkTypeLabel(typeCode).toLowerCase()}…`} />
                    </div>

                    <div className="border border-border rounded-md max-h-72 overflow-y-auto">
                      {suggestLoading ? <LoadingBlock label="Searching…" />
                        : suggestions.length === 0 ? (
                          <p className="p-3 text-erp-xs text-muted-foreground text-center">
                            Nothing matches inside this poll's scope. The list is narrowed by the
                            server to what can legitimately be a subject of a poll fielded here —
                            widen the poll's scope on step 1 if that is wrong.
                          </p>
                        ) : suggestions.map((s) => (
                          <button key={s.ref} type="button" disabled={busyRef === s.ref}
                            onClick={() => toggle(s)}
                            className={cn(
                              'w-full flex items-center gap-2 px-3 py-2 text-left border-b border-border last:border-b-0 transition-colors disabled:opacity-50',
                              s.linked ? 'bg-chocolate-soft/20' : 'hover:bg-muted/40',
                            )}>
                            <span className={cn(
                              'h-4 w-4 rounded border flex items-center justify-center shrink-0',
                              s.linked ? 'bg-chocolate border-chocolate text-white' : 'border-border',
                            )}>
                              {s.linked && <Check className="h-3 w-3" />}
                            </span>
                            <span className="min-w-0 flex-1">
                              <span className="text-erp-sm block truncate">{s.label}</span>
                              {s.secondaryLabel && (
                                <span className="text-erp-xs text-muted-foreground block truncate">
                                  {s.secondaryLabel}
                                </span>
                              )}
                            </span>
                          </button>
                        ))}
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </Panel>
      )}
    </div>
  )
}
